/**
 * Rescue-portal-specific GraphQL queries.
 *
 * These extend the shared appview queries with extra fields the rescue portal
 * needs (e.g. currentUser.email for ownership detection).
 */

import { gql } from '@apollo/client';
import type { ResellerTaxExemptType, viewingChips } from '@/types/gql-op-types';
import type { RescueDeviceOrderAttestationVariant } from '@/types/gql-op-types';
import {
  miniChipFragment,
  basePetFragment,
  contactInfoFragment,
  renderFragments,
  baseChipFragment,
} from '../../lib/fragments';

// ── Response type ───────────────────────────────
// Extends the generated `viewingChips` type with extra fields fetched by
// our rescue-specific query.

type BaseChipItem = viewingChips['currentUser']['chipReseller'] extends infer R
  ? R extends { chips: { chips: Array<infer C> } }
    ? C
    : never
  : never;

type RescueChipItem = BaseChipItem & {
  latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } } | null;
};

export interface RescueViewingChipsData {
  currentUser: {
    id: string;
    email: string;
    chipReseller?: {
      id: string;
      name: string;
      chips: {
        chips: RescueChipItem[];
        metadata?: {
          limit: number;
          offset: number;
          totalChipCount: number;
        } | null;
      };
    } | null;
  };
}

// ── Query ───────────────────────────────────────

/**
 * Like the shared `viewingChipsQuery` but also fetches:
 *  - `currentUser.email` — used to compare against chip contact email
 *  - `latestContactHistoryEntry.contactInfo.primaryEmail` — merged with the
 *     miniChip fragment so we can determine rescue vs adopter ownership at
 *     the list level.
 */
export const rescueViewingChipsQuery = gql`
  query rescueViewingChips($statusType: ChipStatusType!, $limit: Int, $offset: Int) {
    currentUser {
      id
      email
      chipReseller {
        id
        name
        chips(statusType: $statusType, limit: $limit, offset: $offset) {
          chips {
            ...miniChip
            latestContactHistoryEntry {
              contactInfo {
                primaryEmail
              }
            }
          }
          metadata {
            limit
            offset
            totalChipCount
          }
        }
      }
    }
  }

  ${renderFragments(miniChipFragment)}
`;

// ── Search query ──

export interface RescueSearchChipsData {
  currentUser: {
    id: string;
    email: string;
    chipReseller?: {
      id: string;
      searchChips: {
        chips: RescueChipItem[];
      };
    } | null;
  };
}

export const rescueSearchChipsQuery = gql`
  query rescueSearchChips($input: ChipQueryInput!) {
    currentUser {
      id
      email
      chipReseller {
        id
        searchChips(query: $input) {
          chips {
            ...miniChip
            latestContactHistoryEntry {
              contactInfo {
                primaryEmail
              }
            }
          }
        }
      }
    }
  }

  ${renderFragments(miniChipFragment)}
`;

// ── Chip stats (lightweight aggregate counts) ──

export interface RescueChipStatsData {
  currentUser: {
    id: string;
    chipReseller?: {
      id: string;
      stats: {
        totalStock: number;
        totalRegistered: number;
      };
    } | null;
  };
}

/**
 * Lightweight query for aggregate chip counts on the Dashboard.
 *
 * Uses the `stats` resolver on ChipReseller which runs efficient parallel
 * COUNT queries and derives `totalStock` without expensive NOT EXISTS
 * subqueries.  Much faster (and more reliable under tight statement
 * timeouts) than pulling `metadata.totalChipCount` from the paginated
 * `chips` field.
 */
export const rescueChipStatsQuery = gql`
  query rescueChipStats {
    currentUser {
      id
      chipReseller {
        id
        stats {
          totalStock
          totalRegistered
        }
      }
    }
  }
`;

// ── External chip lookup (for pet lookup modal) ──

/**
 * Uses `resellerChip` to look up any chip — available to verified rescues.
 * Returns full pet info, unredacted owner contact info, and the owning
 * reseller's name (organization).
 */
export const externalChipLookupQuery = gql`
  query externalChipLookup($id: ID!) {
    resellerChip(id: $id) {
      id
      shortId
      serialNumber
      status
      updatedAt
      pet {
        ...basePet
      }
      latestContactHistoryEntry {
        source
        contactInfo {
          ...contactInfo
        }
      }
      reseller {
        id
        name
      }
    }
  }

  ${renderFragments(basePetFragment, contactInfoFragment)}
`;

/**
 * Uses `chip` to look up any chip — that may exist but is only available to verified rescues.
 * Returns minimal info
 */
export const externalChipForUnverifiedResellerLookupQuery = gql`
  query chipDetailsExternalLookup($id: ID!) {
    chip(id: $id) {
      ...baseChip
    }
  }

  ${renderFragments(baseChipFragment)}
`;

/**
 * Batch chip-availability lookup for the rescue portal's CSV import
 * wizard. Sends up to 500 serials and gets back a server-side
 * classification (IN_MY_INVENTORY / CLAIMABLE / OTHER_RESELLER /
 * NOT_FOUND / LINKED_TO_PET / NOT_USABLE) per serial. The classification
 * is relative to the authenticated caller's reseller — no
 * `currentResellerId` needs to be sent. Read-only / advisory; the actual
 * claim happens later, per-row, in the async import worker.
 *
 * Backend normalizes the returned `serialNumber` to uppercase, so the
 * frontend should also key results by uppercase serials to match.
 */
export const rescueChipClaimEligibilityQuery = gql`
  query rescueChipClaimEligibility($serialNumbers: [String!]!) {
    rescueChipClaimEligibility(serialNumbers: $serialNumbers) {
      serialNumber
      status
    }
  }
`;

export type RescueChipClaimStatus =
  | 'IN_MY_INVENTORY'
  | 'CLAIMABLE'
  | 'OTHER_RESELLER'
  | 'NOT_FOUND'
  /** Chip exists and is already attached to a pet — re-linking would
   *  detach the existing registration. Backend pre-flight rejects;
   *  surfacing it at upload time saves a publish round-trip. */
  | 'LINKED_TO_PET'
  /** Chip exists but is in a condition that prevents import (recalled,
   *  damaged, decommissioned, etc.). Backend pre-flight rejects; we
   *  surface it inline so the user doesn't burn a publish round-trip. */
  | 'NOT_USABLE';

export interface RescueChipClaimEligibility {
  serialNumber: string;
  status: RescueChipClaimStatus;
}

export interface RescueChipClaimEligibilityData {
  rescueChipClaimEligibility: RescueChipClaimEligibility[];
}

// ── Rescue Devices (offset-paginated, device-bearing pets only) ──

export type RescueDeviceFilter = 'IN_RESCUE' | 'IN_FOSTER' | 'PENDING_ADOPTIONS' | 'COMPLETED_ADOPTIONS';

/**
 * Shared primary-photo selection for the pet-bearing rescue queries (device list
 * + pets list). Both normalize to the same `Pet:<id>`, and `image` is not
 * normalizable, so their `photos` selections MUST be byte-identical — otherwise
 * Apollo reads the narrower one back as `complete: false` and refetches, flashing
 * the pets table, or logs a "Cache data may be lost" warning when the narrower
 * `image` overwrites the wider one. One fragment keeps them from drifting.
 * `thumb` is a server-resized (~96px) URL for list thumbnails; `fullSize` for
 * detail views.
 */
const rescuePetPhotosFragment = {
  symbol: Symbol.for('rescuePetPhotos'),
  body: gql`
    fragment rescuePetPhotos on Pet {
      photos {
        first {
          id
          image {
            fullSize
            thumb: nearestResized(width: 96, height: 96)
          }
        }
      }
    }
  `,
};

/**
 * Paginated list of rescue devices. Uses the dedicated `rescueDevices` query
 * which only returns pets with GPS devices and includes transfer details inline.
 */
/**
 * The row selection both `rescueDevices` documents share.
 *
 * Held as a string rather than a fragment so the paged document can widen it by name: two
 * `pet` selections on the same field merge, which keeps the extra bit next to the reason for
 * it instead of duplicating sixty lines.
 */
const rescueDeviceItemFields = `
        pet {
          id
          name
          gender
          species
          weight
          color
          additionalInfo
          yearOfBirth
          monthOfBirth
          dayOfBirth
          breed {
            name
          }
          chip {
            id
            shortId
            serialNumber
            latestContactHistoryEntry {
              contactInfo {
                primaryEmail
              }
            }
          }
          device {
            id
            moduleId
            currentCollarModelLongName
            hasActiveSubscription
            lastConnectionState {
              date
            }
          }
          ...rescuePetPhotos
        }
        transfer {
          id
          status
          type
          toName
          toFullName
          toEmail
          toPhone
          completedAt
          stalledAt
          createdAt
          updatedAt
        }
        scheduledHandoff {
          id
          kind
          status
          scheduledFor
          scheduledForTimezone
          toFirstName
          toLastName
        }
        batteryPercent
        isOnline
        hasSafeZone
        hasWifiSetup
`;

/**
 * Paginated list of rescue devices. Uses the dedicated `rescueDevices` query
 * which only returns pets with GPS devices and includes transfer details inline.
 *
 * The lean document, and the one the drained callers take. It deliberately omits
 * `pet.rescueAdoptionState` — see `rescueDevicesPagedQuery`.
 */
export const rescueDevicesQuery = gql`
  query rescueDeviceList($input: RescueDevicesInput!) {
    rescueDevices(input: $input) {
      items {
        ${rescueDeviceItemFields}
      }
      totalCount
      hasMore
    }
  }
  ${renderFragments(rescuePetPhotosFragment)}
`;

/**
 * The same rows plus the per-row adoption gate, for the sections that render one.
 *
 * Separate from the document above because `Pet.rescueAdoptionState` resolves per pet on the
 * server — `findPetAdoptionState` request-caches the caller's household ids but still runs a
 * lookup for every row. The drained callers fetch a hundred rows a page and none of them read
 * the field, so putting it on the shared document billed those drains a query per row for
 * nothing.
 */
export const rescueDevicesPagedQuery = gql`
  query rescueDevicePage($input: RescueDevicesInput!) {
    rescueDevices(input: $input) {
      items {
        ${rescueDeviceItemFields}
        pet {
          rescueAdoptionState
        }
      }
      totalCount
      hasMore
    }
  }
  ${renderFragments(rescuePetPhotosFragment)}
`;

export interface RescueDeviceItemPetDeviceGql {
  id: string;
  moduleId: string;
  currentCollarModelLongName?: string | null;
  hasActiveSubscription: boolean;
  lastConnectionState?: {
    date: string;
  } | null;
}

/**
 * Where a pet sits in the calling rescue's adoption pipeline. Pet-centric, so unlike the
 * `rescueDevices` filters this used to be cross-referenced against, it also reports a
 * chip-only adoption — one where no device was ever attached.
 */
export type RescuePetAdoptionState = 'NONE' | 'PENDING' | 'COMPLETED';

export interface RescueDeviceItemPetGql {
  id: string;
  name: string;
  /**
   * Absent on `rescueDevicesQuery`, which omits it deliberately — only the paged document
   * selects it. Optional so a caller reading it off a drained row has to say what an
   * unfetched answer means rather than getting `undefined` typed as a verdict.
   */
  rescueAdoptionState?: RescuePetAdoptionState;
  photos?: RescuePetPhotos | null;
  gender?: string | null;
  species?: string | null;
  weight?: number | null;
  color?: string | null;
  additionalInfo?: string | null;
  yearOfBirth?: number | null;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
  breed?: { name: string } | null;
  chip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  } | null;
  device?: RescueDeviceItemPetDeviceGql | null;
}

/**
 * The pet as {@link rescuePetQuery} selects it. Narrower than a device-list row on purpose: the
 * overlay reads the module id and nothing else off the collar, and the wider row type requires
 * fields this document does not ask for — including a deprecated one.
 */
export interface RescuePlacementPetGql extends Omit<RescueDeviceItemPetGql, 'device'> {
  device?: { id: string; moduleId: string } | null;
}

export interface RescuePetData {
  rescuePet: {
    pet: RescuePlacementPetGql;
    /**
     * The pet's current rescue transfer, which is what proves a placement row is about the pet the
     * caller thinks it is. A ledger row can outlive the stay it records — an admin household move
     * or a boarding move leaves it behind — so the name match that picked this pet is checked
     * against where the pet actually is.
     */
    transfer: { id: string; type: string; toHousehold: { id: string } | null } | null;
  } | null;
}

/**
 * One pet, by id, in the shape the pet-details overlay renders.
 *
 * Deliberately its own document rather than a fragment shared with `rescueDevicesQuery`. The two
 * happen to want the same fields today, but that query is fetched a page at a time across the
 * portal, and anything added to a shared selection would be billed to every one of those rows.
 * Keeping them apart means this one can grow for the overlay without the lists paying for it.
 *
 * Resolves for a pet placed with a foster: the rescue user is an owner of every foster household,
 * a membership that exists so the portal can reach the pets it has placed out.
 */
export const rescuePetQuery = gql`
  query rescuePet($petId: ID!) {
    rescuePet(petId: $petId) {
      pet {
        id
        name
        gender
        species
        weight
        color
        additionalInfo
        yearOfBirth
        monthOfBirth
        dayOfBirth
        breed {
          name
        }
        chip {
          id
          shortId
          serialNumber
          latestContactHistoryEntry {
            contactInfo {
              primaryEmail
            }
          }
        }
        device {
          id
          moduleId
        }
        ...rescuePetPhotos
      }
      transfer {
        id
        type
        toHousehold {
          id
        }
      }
    }
  }
  ${renderFragments(rescuePetPhotosFragment)}
`;

export interface RescueDeviceItemTransferGql {
  id: string;
  status: string;
  type: string;
  toName?: string | null;
  toFullName?: string | null;
  toEmail?: string | null;
  toPhone?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  stalledAt?: string | null;
}

export interface RescueScheduledHandoffGql {
  id: string;
  kind: 'ADOPTION' | 'FOSTER' | 'ADOPTION_FROM_FOSTER';
  status: 'SCHEDULED' | 'EXECUTING' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  scheduledFor: string;
  scheduledForTimezone: string;
  toFirstName?: string | null;
  toLastName?: string | null;
  toEmail?: string | null;
  toPhone?: string | null;
  notes?: string | null;
  failureReason?: string | null;
  chipFailureReason?: string | null;
  createdAt?: string | null;
  pet?: { id: string; name: string | null } | null;
}

export interface RescueScheduledHandoffsData {
  rescueScheduledHandoffs: RescueScheduledHandoffGql[];
}

export interface RescueDeviceItemGql {
  pet: RescueDeviceItemPetGql;
  transfer?: RescueDeviceItemTransferGql | null;
  scheduledHandoff?: RescueScheduledHandoffGql | null;
  batteryPercent: number | null;
  isOnline: boolean;
  hasSafeZone: boolean;
  hasWifiSetup: boolean;
}

export interface RescueDevicesData {
  rescueDevices: {
    items: RescueDeviceItemGql[];
    totalCount: number;
    hasMore: boolean;
  };
}

// ── Rescue Order Shipments (devices in transit) ──

export type RescueOrderShipmentStatus = 'ORDERED' | 'SHIPPED' | 'DELIVERED' | 'CANCELED';

export interface RescueOrderShipmentGql {
  orderNumbers: string[];
  shipmentStatus: RescueOrderShipmentStatus;
  deviceCount: number;
  orderDate: string;
  trackingNumber?: string | null;
}

export interface RescueOrderShipmentsData {
  rescueOrderShipments: RescueOrderShipmentGql[];
}

/**
 * Outstanding (non-delivered) shipments for the current rescue user. Each
 * shipment surfaces only what the rescue portal renders; orders with multiple
 * shipments will appear once per still-shipping shipment.
 */
export const rescueOrderShipmentsQuery = gql`
  query rescueOrderShipments {
    rescueOrderShipments {
      orderNumbers
      shipmentStatus
      deviceCount
      orderDate
      trackingNumber
    }
  }
`;

// ── Rescue Nano Shipments (chips in transit) ──

export interface RescueNanoShipmentGql {
  orderNumbers: string[];
  shipmentStatus: RescueOrderShipmentStatus;
  chipCount: number;
  orderDate: string;
  trackingNumber?: string | null;
}

export interface RescueNanoShipmentsData {
  rescueNanoShipments: RescueNanoShipmentGql[];
}

/**
 * Outstanding (non-delivered) nano shipments for the current rescue user.
 * Same in-flight semantics as `rescueOrderShipments` but limited to shipments
 * containing a Fi Nano line item, with chip counts in place of device counts.
 */
export const rescueNanoShipmentsQuery = gql`
  query rescueNanoShipments {
    rescueNanoShipments {
      orderNumbers
      shipmentStatus
      chipCount
      orderDate
      trackingNumber
    }
  }
`;

// ── GWA Dashboard Stats (lightweight) ────────────

export interface RescueGwaDashboardStatsData {
  rescueGwaProgram: {
    id: string;
    monthRedemptions: number;
    totalRedemptions: number;
  } | null;
}

/**
 * Lightweight query for the Dashboard's GWA Redemptions stat card.
 * Skips the expensive `recentRedemptions` field (which fans out into
 * per-adopter user/pet/breed lookups) — the full query on the GWA page
 * will pick those up when the user navigates there.
 */
export const rescueGwaDashboardStatsQuery = gql`
  query rescueGwaDashboardStats {
    rescueGwaProgram {
      id
      monthRedemptions
      totalRedemptions
    }
  }
`;

// ── Rescue Device Stats ──────────────────────────

/**
 * Aggregate counts for all filter categories. Used for the summary stats cards.
 * Returns accurate device-only counts in a single round-trip.
 */
export const rescueDeviceStatsQuery = gql`
  query rescueDeviceStats($completedSince: DateTime) {
    rescueDeviceStats(completedSince: $completedSince) {
      inRescueCount
      inFosterCount
      pendingAdoptionsCount
      completedAdoptionsCount
      scheduledHandoffsCount
      totalActiveDevices
      stalledTransferThresholdDays
    }
  }
`;

export interface RescueDeviceStatsGql {
  scheduledHandoffsCount?: number | null;
  inRescueCount: number;
  inFosterCount: number;
  pendingAdoptionsCount: number;
  completedAdoptionsCount: number;
  totalActiveDevices: number;
  /**
   * How long a pending adoption may sit before the server counts it as stalled. The same
   * number drives the `stalled` filter and the Stalled section's copy, so the page reads it
   * here rather than keeping a second copy that can drift from the predicate.
   */
  stalledTransferThresholdDays: number;
}

export interface RescueDeviceStatsData {
  rescueDeviceStats: RescueDeviceStatsGql;
}

// ── Rescue create device order (Order Devices modal) ──

export interface RescueOrgShippingAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  country: string;
  zip: string;
}

export interface RescueCreateDeviceOrderInput {
  contactName: string;
  contactEmail: string;
  deviceQuantity: number;
  includeHardCases: boolean;
  shippingAddress: RescueOrgShippingAddress;
  notes?: string | null;
  /** The version served by `rescueDevicesProgram`, echoed back to prove which wording was agreed to. */
  attestationVersion: string;
  attestationAgreed: boolean;
}

export interface RescueCreateDeviceOrderData {
  rescueCreateDeviceOrder: {
    ok: boolean;
    orderId: string;
  };
}

/**
 * Submit a device-order request for the current rescue. The backend issues a
 * synthetic intake number now and the canonical Order ID once real orders
 * back this mutation.
 */
export const rescueCreateDeviceOrderMutation = gql`
  mutation rescueCreateDeviceOrder($input: RescueCreateDeviceOrderInput!) {
    rescueCreateDeviceOrder(input: $input) {
      ok
      orderId
    }
  }
`;

// ── Rescue create nano order (Order Nanos modal) ──

export interface RescueCreateNanoOrderInput {
  contactName: string;
  contactEmail: string;
  /** Number of 20-chip boxes (Fi Nano `F1-N-20` SKU). Backend bounds: 1–25. */
  boxQuantity: number;
  shippingAddress: RescueOrgShippingAddress;
  notes?: string | null;
}

export interface RescueCreateNanoOrderData {
  rescueCreateNanoOrder: {
    ok: boolean;
    orderId: string;
  };
}

/** Submit a nano-microchip-order request for the current rescue. */
export const rescueCreateNanoOrderMutation = gql`
  mutation rescueCreateNanoOrder($input: RescueCreateNanoOrderInput!) {
    rescueCreateNanoOrder(input: $input) {
      ok
      orderId
    }
  }
`;

// ── GWA postcards (preview + order + list) ──────

/**
 * Backend order lifecycle. Self-service orders start in `pending_approval` and only reach
 * `queued` once ops approves; the portal collapses the pre-production states into one
 * "Processing" badge.
 */
export type RescueGwaPostcardOrderStatus =
  | 'pending_approval'
  | 'queued'
  | 'submitted'
  | 'in_production'
  | 'shipped'
  | 'delivered'
  | 'failed'
  | 'cancelled';

export interface RescueGwaPostcardOrderGql {
  id: string;
  status: RescueGwaPostcardOrderStatus;
  quantity: number;
  createdAt: string;
  /** Null until the vendor reports one, which can lag the `shipped` status by a poll cycle. */
  trackingNumber: string | null;
}

export interface RescueGwaPostcardsData {
  rescueGwaPostcardOrders: RescueGwaPostcardOrderGql[];
  /**
   * Cards are in a closed beta, gated on a user group the backend owns. The order mutation
   * returns Forbidden when this is false, so the ordering UI has to hide rather than fail late.
   * Order history stays readable either way — a rescue dropped from the beta keeps its records.
   */
  rescueGwaPostcardsEligible: boolean;
}

/** Card orders for the current rescue's GWA program, most recent first, plus beta eligibility. */
export const rescueGwaPostcardsQuery = gql`
  query rescueGwaPostcards {
    rescueGwaPostcardOrders {
      id
      status
      quantity
      createdAt
      trackingNumber
    }
    rescueGwaPostcardsEligible
  }
`;

/** CARDS_WITH_DEVICES carves out dogs that go home wearing a Fi Mini; web picks it from devices enrollment. */
export type RescueGwaPostcardAttestationVariant = 'CARDS_WITH_DEVICES' | 'CARDS_ONLY';

export interface GwaPostcardAttestationTerms {
  variant: RescueGwaPostcardAttestationVariant;
  version: string;
  verifyHeading: string;
  verifyBullets: string[];
  understandHeading: string;
  understandBullets: string[];
  agreementLabel: string;
}

export interface RescueGwaPostcardAttestationData {
  rescueGwaPostcardAttestation: GwaPostcardAttestationTerms;
}

// Its own query rather than a field on `rescueGwaPostcards`, so a failure here can't take down the GWA page.
export const rescueGwaPostcardAttestationQuery = gql`
  query rescueGwaPostcardAttestation {
    rescueGwaPostcardAttestation {
      variant
      version
      verifyHeading
      verifyBullets
      understandHeading
      understandBullets
      agreementLabel
    }
  }
`;

export interface RescueRequestGwaPostcardsInput {
  /** Backend bounds: 100–500, in packs of 100. */
  quantity: number;
  contactName: string;
  contactEmail: string;
  shippingAddress: RescueOrgShippingAddress;
  notes?: string | null;
  /** The `version` served by `rescueGwaPostcardAttestation`, echoed back. */
  attestationVersion: string;
  attestationAgreed: boolean;
}

export interface RescueRequestGwaPostcardsData {
  rescueRequestGwaPostcards: RescueGwaPostcardOrderGql;
}

/**
 * Request a batch of printed GWA postcards shipped to the rescue. The order holds for ops
 * approval before printing, mirroring device/chip order requests.
 */
export const rescueRequestGwaPostcardsMutation = gql`
  mutation rescueRequestGwaPostcards($input: RescueRequestGwaPostcardsInput!) {
    rescueRequestGwaPostcards(input: $input) {
      id
      status
      quantity
      createdAt
      trackingNumber
    }
  }
`;

// ── Chip condition mutation ─────────────────────

/**
 * Update a chip's condition (e.g. mark as DAMAGED, DECEASED_PET, MISSING).
 * Non-WORKING chips are filtered out of all reseller chip lists by the backend,
 * so this is effectively a one-way "remove from board" action.
 */
export const updateChipConditionMutation = gql`
  mutation rescueUpdateChipCondition($chipId: ID!, $chipCondition: ChipCondition!) {
    updateChipCondition(input: { chipId: $chipId, chipCondition: $chipCondition }) {
      id
      shortId
      serialNumber
      chipCondition
    }
  }
`;

// ── Rescue device mutations ─────────────────────

/** Create a foster transfer — moves pet to a foster caretaker. */
export const rescueCreatePetFosterTransferMutation = gql`
  mutation rescueCreatePetFosterTransfer($input: RescueCreatePetFosterTransferInput!) {
    rescueCreatePetFosterTransfer(input: $input) {
      __typename
    }
  }
`;

/** Recall a pet from a foster back to the rescue org. */
export const rescueRecallPetFosterTransferMutation = gql`
  mutation rescueRecallPetFosterTransfer($input: RescueRecallPetFosterTransferInput!) {
    rescueRecallPetFosterTransfer(input: $input) {
      __typename
    }
  }
`;

// ── Foster roster (persisted foster homes) ──────

/** A rescue's saved foster home. Outlives any single placement. */
export interface RescueFosterHomeGql {
  id: string;
  contactFirstName: string;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhoneNumber: string | null;
  hasWifiSetup: boolean;
  hasSafeZone: boolean;
  /** Lifetime total, counted from the ledger server-side — exactly `placements.length`. */
  placementCount: number;
  /**
   * Stays currently open, counted server-side. The roster's Status column reads this and
   * `rescueFosterHomesPaged` orders on it, so deriving it from `placements` here as well would
   * let the number under a row disagree with the position the server gave that row.
   */
  activePlacementCount: number;
  /** Every recorded stay, newest first. */
  placements: RescueFosterPlacementGql[];
  createdAt: string;
  fosterHousehold: {
    id: string;
    /** Placed right now. Resolves to `[]` rather than throwing when the caller lacks access. */
    pets: Array<{ id: string; name: string }>;
    /** Same silent-empty behaviour as `pets` when the caller lacks access. */
    owners: HouseholdMemberGql[];
    caretakers: HouseholdMemberGql[];
  };
}

export interface RescueFosterPlacementGql {
  id: string;
  /** The pet's name when placed, which is not necessarily its name today. */
  petName: string;
  fosteredAt: string;
  /** Null while the pet is still there. */
  recalledAt: string | null;
  endedReason: 'RECALLED' | 'ADOPTED' | 'UNKNOWN' | null;
}

export interface HouseholdMemberGql {
  userId: string;
  firstName: string;
  lastName: string | null;
}

/** Shared by the list query and every mutation that returns a home. */
const fosterHomeFields = `
  id
  contactFirstName
  contactLastName
  contactEmail
  contactPhoneNumber
  hasWifiSetup
  hasSafeZone
  placementCount
  activePlacementCount
  placements {
    id
    petName
    fosteredAt
    recalledAt
    endedReason
  }
  createdAt
  fosterHousehold {
    id
    pets {
      id
      name
    }
    owners: users(role: OWNER) {
      userId
      firstName
      lastName
    }
    caretakers: users(role: CARETAKER) {
      userId
      firstName
      lastName
    }
  }
`;

export interface RescueFosterHomesData {
  rescueFosterHomes: RescueFosterHomeGql[];
}

/**
 * Active homes, newest first, unpaginated. Includes homes with a pet placed and homes without.
 *
 * The whole-list shape, and a permanent one: the assign-to-foster picker needs every home to
 * offer, and the Fosters tab resolves each placement row's home out of it. The roster *table*
 * reads `rescueFosterHomesPaged` instead — see below.
 */
export const rescueFosterHomesQuery = gql`
  query rescueFosterHomes {
    rescueFosterHomes {
      ${fosterHomeFields}
    }
  }
`;

/** Columns `rescueFosterHomesPaged` can order by, in the schema's spelling. */
export type RescueFosterHomeSortKey = 'FOSTER' | 'STATUS' | 'PLACEMENTS';

export interface RescueFosterHomesPagedData {
  rescueFosterHomesPaged: {
    items: RescueFosterHomeGql[];
    totalCount: number;
    hasMore: boolean;
  };
}

/**
 * One page of the roster, ordered by Postgres. Alongside `rescueFosterHomes` rather than
 * replacing it: a table wants a page, a picker wants every home, and the two are different
 * questions asked of the same records.
 */
export const rescueFosterHomesPagedQuery = gql`
  query rescueFosterHomesPaged($input: RescueFosterHomesInput!) {
    rescueFosterHomesPaged(input: $input) {
      items {
        ${fosterHomeFields}
      }
      totalCount
      hasMore
    }
  }
`;

export interface RescueFosterHomeForContactData {
  rescueFosterHomeForContact: RescueFosterHomeGql | null;
}

/**
 * The home this contact maps to, or null. Shares the matcher the create/placement paths reuse from,
 * so it can't disagree with what a save would do. BadRequest without an email or phone.
 */
export const rescueFosterHomeForContactQuery = gql`
  query rescueFosterHomeForContact($email: String, $phoneNumber: String) {
    rescueFosterHomeForContact(email: $email, phoneNumber: $phoneNumber) {
      ${fosterHomeFields}
    }
  }
`;

/** Email and phone are both required here, unlike the nullable contact snapshot. */
export interface RescueCreateFosterHomeInput {
  contactFirstName: string;
  contactLastName?: string | null;
  contactEmail: string;
  contactPhoneNumber: string;
}

export interface RescueCreateFosterHomeData {
  rescueCreateFosterHome: RescueFosterHomeGql;
}

/**
 * Save a foster home ahead of any placement. Contacts nobody — the volunteer is invited when a pet
 * is placed. Reuse-first: an existing contact gets that home back refreshed, so a retry is safe.
 */
export const rescueCreateFosterHomeMutation = gql`
  mutation rescueCreateFosterHome($input: RescueCreateFosterHomeInput!) {
    rescueCreateFosterHome(input: $input) {
      ${fosterHomeFields}
    }
  }
`;

export interface RescueArchiveFosterHomeData {
  rescueArchiveFosterHome: { id: string };
}

/**
 * Retire a home: tear the household down (places, wifi, memberships) and archive the record.
 * Rejected while a pet is still placed.
 */
export const rescueArchiveFosterHomeMutation = gql`
  mutation rescueArchiveFosterHome($id: ID!) {
    rescueArchiveFosterHome(id: $id) {
      id
    }
  }
`;

/** Unassign a device from a pet (Remove Pet). */
export const unassignDeviceMutation = gql`
  mutation unassignDevice($moduleId: String!, $petId: ID!) {
    unassignDevice(moduleId: $moduleId, petId: $petId) {
      id
    }
  }
`;

// ── Scheduled hand-offs ─────────────────────

const SCHEDULED_HANDOFF_FIELDS = `
  id
  kind
  status
  scheduledFor
  scheduledForTimezone
  toFirstName
  toLastName
  toEmail
  toPhone
  notes
  failureReason
  chipFailureReason
  createdAt
  pet {
    id
    name
  }
`;

/** Every hand-off this rescue has booked for a future time. */
export const rescueScheduledHandoffsQuery = gql`
  query rescueScheduledHandoffs {
    rescueScheduledHandoffs {
      ${SCHEDULED_HANDOFF_FIELDS}
    }
  }
`;

/**
 * Book an adoption or foster placement for later. Writes the intent only — the collar, household,
 * and adopter invite are all untouched until the scheduled time.
 */
export const rescueScheduleHandoffMutation = gql`
  mutation rescueScheduleHandoff($input: RescueScheduleHandoffInput!) {
    rescueScheduleHandoff(input: $input) {
      handoff {
        ${SCHEDULED_HANDOFF_FIELDS}
      }
    }
  }
`;

export const rescueRescheduleHandoffMutation = gql`
  mutation rescueRescheduleHandoff($input: RescueRescheduleHandoffInput!) {
    rescueRescheduleHandoff(input: $input) {
      handoff {
        ${SCHEDULED_HANDOFF_FIELDS}
      }
    }
  }
`;

export const rescueCancelScheduledHandoffMutation = gql`
  mutation rescueCancelScheduledHandoff($input: RescueCancelScheduledHandoffInput!) {
    rescueCancelScheduledHandoff(input: $input) {
      handoff {
        id
        status
      }
    }
  }
`;

export const rescueCreatePetAdoptionTransferMutation = gql`
  mutation rescueCreatePetAdoptionTransfer($input: RescueCreatePetAdoptionTransferInput!) {
    rescueCreatePetAdoptionTransfer(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/** Cancel a pending adoption transfer — returns the device to the rescue. */
export const rescueCancelPetAdoptionTransferMutation = gql`
  mutation rescueCancelPetAdoptionTransfer($input: RescueCancelPetAdoptionTransferInput!) {
    rescueCancelPetAdoptionTransfer(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/**
 * Mark a stalled pending adoption as "not recovered" — the rescue steps out of
 * the adoption household, but the collar stays attached to its pet so a future
 * owner can still claim it and set up a subscription.
 */
export const rescueMarkAdoptionTransferNotRecoveredMutation = gql`
  mutation rescueMarkAdoptionTransferNotRecovered($input: RescueMarkAdoptionTransferNotRecoveredInput!) {
    rescueMarkAdoptionTransferNotRecovered(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/**
 * Mark a stalled pending adoption as "recovered" — the rescue got the collar
 * back. Closes the transfer and returns the device to unassigned stock.
 */
export const rescueMarkAdoptionTransferRecoveredMutation = gql`
  mutation rescueMarkAdoptionTransferRecovered($input: RescueMarkAdoptionTransferRecoveredInput!) {
    rescueMarkAdoptionTransferRecovered(input: $input) {
      transfer {
        id
        status
      }
    }
  }
`;

/**
 * Flag a still-pending adoption as "stalled" so it surfaces for follow-up before
 * it ages into the automatic 7-day stalled window. Does not resolve the transfer.
 */
export const rescueMarkAdoptionTransferStalledMutation = gql`
  mutation rescueMarkAdoptionTransferStalled($input: RescueMarkAdoptionTransferStalledInput!) {
    rescueMarkAdoptionTransferStalled(input: $input) {
      transfer {
        id
        status
        stalledAt
      }
    }
  }
`;

/** Send an SMS to a foster holder or adopter. */
export const rescueSendSmsMutation = gql`
  mutation rescueSendSms($input: RescueSendSmsInput!) {
    rescueSendSms(input: $input) {
      ok
    }
  }
`;

// ── Devices program enrollment ─────────────────

/**
 * The commitment terms for the rescue's next device order, served whole so the wording and the
 * version it's recorded under can't disagree. `{rescue}` is substituted client-side and `**phrase**`
 * renders as emphasis — see the schema docs on `RescueDeviceOrderAttestation`.
 */
export interface DeviceOrderAttestationTerms {
  variant: RescueDeviceOrderAttestationVariant;
  version: string;
  verifyHeading: string;
  verifyBullets: string[];
  understandHeading: string;
  understandBullets: string[];
  agreementLabel: string;
  scopeNote: string | null;
}

/**
 * A device order the rescue has filed that Fi hasn't approved or rejected yet. Its presence means
 * `rescueCreateDeviceOrder` will reject a new order, so the portal shows this instead of the order
 * form rather than letting a rescue fill one out that can't be filed.
 *
 * It clears when ops acts on the order — there's no timeout that releases it, so there's no wait to
 * quote the rescue.
 */
export interface PendingDeviceOrder {
  /** Same id the order confirmation showed, so the rescue can match the two up. */
  orderId: string;
  /** ISO-8601 from the `DateTime` scalar. */
  submittedAt: string;
  /** Fi Minis requested — excludes the hard-case packs shipping alongside them. */
  deviceQuantity: number;
  /**
   * Server-rendered copy for the blocked state — the same sentence `rescueCreateDeviceOrder`
   * returns with its 409. Render it as-is rather than composing our own: it's what the rescue sees
   * if they get past us and submit anyway, and it saves us pluralizing a count in two places.
   */
  message: string;
}

export interface RescueDevicesProgramData {
  rescueDevicesProgram: {
    enrolled: boolean;
    /**
     * Which commitment terms the rescue's next device order requires, and the version id to echo
     * back on submit. STARTER_BATCH until they've placed one device order through the portal.
     */
    deviceOrderAttestation: DeviceOrderAttestationTerms;
    /** Null when the rescue has no order awaiting review — the normal, orderable state. */
    pendingDeviceOrder: PendingDeviceOrder | null;
  };
}

export const rescueDevicesProgramQuery = gql`
  query rescueDevicesProgram {
    rescueDevicesProgram {
      enrolled
      deviceOrderAttestation {
        variant
        version
        verifyHeading
        verifyBullets
        understandHeading
        understandBullets
        agreementLabel
        scopeNote
      }
      pendingDeviceOrder {
        orderId
        submittedAt
        deviceQuantity
        message
      }
    }
  }
`;

export const rescueEnrollDevicesProgramMutation = gql`
  mutation rescueEnrollDevicesProgram {
    rescueEnrollDevicesProgram {
      enrolled
    }
  }
`;

// Self-serve rescue enrollment reuses the existing registerChipResellerV2 mutation: it creates a
// RESCUE-typed reseller (Accepted, unverified) for the signed-in user from the submitted form and
// auto-enrolls them in the rescue org group. We send `name` = the rescue household name.
export interface RescueResellerContactInput {
  firstName: string;
  lastName?: string | null;
  primaryPhone?: string | null;
  primaryEmail: string;
  website?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country: string;
}

export interface RegisterChipResellerV2Input {
  name: string;
  /** ResellerType values; rescue enrollment sends ['RESCUE']. */
  types: string[];
  /** ResellerPetInterest values; sent empty for rescue enrollment. */
  petInterestTypes: string[];
  contactInfo: RescueResellerContactInput;
  shippingAddress: RescueOrgShippingAddress;
}

export interface RegisterChipResellerV2Data {
  registerChipResellerV2: {
    id: string;
    name: string;
    verified: boolean;
  };
}

export const registerChipResellerV2Mutation = gql`
  mutation registerChipResellerV2($input: RegisterChipResellerInputV2!) {
    registerChipResellerV2(input: $input) {
      id
      name
      verified
    }
  }
`;

export const rescueUnenrollDevicesProgramMutation = gql`
  mutation rescueUnenrollDevicesProgram {
    rescueUnenrollDevicesProgram {
      enrolled
    }
  }
`;

// ── Assign device to pet ───────────────────────

/** Assign a GPS device to a pet by module ID. */
export const assignDeviceMutation = gql`
  mutation assignDevice($moduleId: String!, $petId: ID!) {
    assignDevice(moduleId: $moduleId, petId: $petId) {
      id
      name
    }
  }
`;

/**
 * Move a device already attached to one pet onto another, atomically. Unlike
 * `assignDevice`, the backend's `reassignDevice` calls `removeDeviceFromPet`
 * on the source pet before assigning to the target, so it works even when the
 * device has an active subscription that `isAssignable` would otherwise refuse.
 */
export const reassignDeviceMutation = gql`
  mutation reassignDevice($moduleId: String!, $toPetId: ID!) {
    reassignDevice(moduleId: $moduleId, toPetId: $toPetId) {
      id
      name
    }
  }
`;

export interface CheckDeviceAssignableData {
  checkDeviceAssignable: {
    assignable: boolean;
    reason?: string | null;
  };
}

/**
 * Reports whether the backend currently considers a device assignable. Used
 * client-side to gate the rescue's "assign device by serial" flow against
 * silently stealing a device off another household. Note: the backend's
 * `module:assignableWithoutSubscription` flag treats no-active-subscription
 * devices as assignable even when attached to another pet, so this gate
 * doesn't catch that edge case.
 */
export const checkDeviceAssignableQuery = gql`
  query checkDeviceAssignable($moduleId: String!) {
    checkDeviceAssignable(moduleId: $moduleId) {
      assignable
      reason
    }
  }
`;

/** Remove a pet from the household. */
export const removePetMutation = gql`
  mutation removePet($subjectId: ID!) {
    removePet(subjectId: $subjectId)
  }
`;

// ── Remove chip from pet ───────────────────────

/**
 * Detach a chip from its currently-attached pet and return it to the rescue's
 * stock (SHIPPED status). Clears both pet and owner/contact info.
 *
 * Used when a chip was incorrectly registered to a pet and needs to be made
 * available again. Distinct from `recallChip` (which clears only the owner
 * and preserves pet info) and `reassignChipToReseller` (which is for
 * claiming a chip from a different reseller).
 */
export const removeChipFromPetMutation = gql`
  mutation removeChipFromPet($input: RemoveChipFromPetInput!) {
    removeChipFromPet(input: $input) {
      id
      serialNumber
      shortId
    }
  }
`;

export interface RemoveChipFromPetInput {
  chipId: string;
}

export interface RemoveChipFromPetData {
  removeChipFromPet: {
    id: string;
    serialNumber: string;
    shortId: string;
  };
}

// ── Rescue pets (for pet selection) ────────────

export interface RescuePetOption {
  id: string;
  name: string;
  hasDevice: boolean;
}

/** Primary photo shape shared by the pet and device-item selections. `thumb` is
 *  a server-resized (~96px) URL for list thumbnails; `fullSize` for detail views. */
export interface RescuePetPhotos {
  first?: {
    id: string;
    image: { fullSize?: string | null; thumb?: string | null };
  } | null;
}

export interface RescuePetGql {
  id: string;
  name: string;
  photos?: RescuePetPhotos | null;
  gender?: string | null;
  species?: string | null;
  weight?: number | null;
  color?: string | null;
  updatedAt?: string | null;
  additionalInfo?: string | null;
  device?: { id: string; moduleId: string; lastConnectionState?: { date: string } | null } | null;
  chip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    /** Selected here too, so the roster's two chip sources gate the same way. */
    isFiChip?: boolean;
    latestContactHistoryEntry?: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  } | null;
  breed?: { name: string } | null;
  yearOfBirth?: number | null;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
}

export interface RescuePetsData {
  currentUser: {
    id: string;
    userHouseholds: {
      household: {
        id: string;
        type: string;
        pets: RescuePetGql[];
      };
    }[];
  };
}

/** Fetch all pets in the user's RESCUE household for selection dropdowns. */
export const rescuePetsQuery = gql`
  query rescuePets {
    currentUser {
      id
      userHouseholds {
        household {
          id
          type
          pets {
            id
            name
            gender
            species
            weight
            color
            additionalInfo
            updatedAt
            device {
              id
              moduleId
              lastConnectionState {
                date
              }
            }
            chip {
              id
              shortId
              serialNumber
              latestContactHistoryEntry {
                contactInfo {
                  primaryEmail
                }
              }
            }
            breed {
              name
            }
            yearOfBirth
            monthOfBirth
            dayOfBirth
            ...rescuePetPhotos
          }
        }
      }
    }
  }
  ${renderFragments(rescuePetPhotosFragment)}
`;

// ── Rescue roster (the Dashboard's paginated pet list) ──

export type RescueRosterStatus = 'RESCUE' | 'FOSTER' | 'PENDING_ADOPTION' | 'ADOPTED';

/** The roster's status chips. ADOPTED covers pending and completed, matching how the UI reads. */
export type RescueRosterStatusFilter = 'RESCUE' | 'FOSTER' | 'ADOPTED';

/** Columns `rescueRoster` can order by, in the schema's spelling. */
export type RescueRosterSortKey = 'NAME' | 'CHIP' | 'DEVICE' | 'STATUS';

export interface RescueRosterRowGql {
  pet: RescuePetGql;
  status: RescueRosterStatus;
  /** The timestamp the Status column reads; null when the underlying record has none. */
  updatedAt: string | null;
  /** The adopter snapshot on a PENDING_ADOPTION row; null for every other status. */
  pendingAdoption: {
    toFullName: string | null;
    toEmail: string | null;
    toPhone: string | null;
  } | null;
  /**
   * Matched by pet rather than through `Pet.chip`, which goes null for pending and adopted
   * pets once the rescue's household membership lapses even though the rescue still owns the
   * chip. This is what the Dashboard used to reconstruct by draining the reseller's chips.
   */
  chip: {
    id: string;
    shortId: string;
    serialNumber: string;
    /**
     * The server matches by pet and deliberately skips household gating, so this can be a
     * microchip the rescue has nothing to do with. The Chips page lists the reseller's own
     * chips, so a non-Fi one is not findable there — see `adaptRosterRow`.
     */
    isFiChip: boolean;
    latestContactHistoryEntry: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  } | null;
}

export interface RescueRosterData {
  rescueRoster: {
    items: RescueRosterRowGql[];
    totalCount: number;
    hasMore: boolean;
  };
}

/**
 * One page of the rescue's pets with the status the Dashboard shows, resolved server-side.
 *
 * Distinct from `rescuePetsQuery`, which stays: that one is the picker source for the assign
 * and register modals, and wants every pet in the household rather than a page of a roster
 * that also includes pets the rescue has already placed or adopted out.
 */
export const rescueRosterQuery = gql`
  query rescueRoster($input: RescueRosterInput!) {
    rescueRoster(input: $input) {
      items {
        pet {
          id
          name
          gender
          species
          weight
          color
          additionalInfo
          updatedAt
          device {
            id
            moduleId
            lastConnectionState {
              date
            }
          }
          chip {
            id
            shortId
            serialNumber
            isFiChip
            latestContactHistoryEntry {
              contactInfo {
                primaryEmail
              }
            }
          }
          breed {
            name
          }
          yearOfBirth
          monthOfBirth
          dayOfBirth
          ...rescuePetPhotos
        }
        status
        updatedAt
        pendingAdoption {
          toFullName
          toEmail
          toPhone
        }
        chip {
          id
          shortId
          serialNumber
          isFiChip
          latestContactHistoryEntry {
            contactInfo {
              primaryEmail
            }
          }
        }
      }
      totalCount
      hasMore
    }
  }
  ${renderFragments(rescuePetPhotosFragment)}
`;

// ── Breeds (for pet creation) ──────────────────

export interface BreedOption {
  id: string;
  name: string;
  species: string;
}

export interface AllBreedsData {
  allBreeds: BreedOption[];
}

/** Fetch all breeds, optionally filtered by species. */
export const allBreedsQuery = gql`
  query rescueAllBreeds($species: Species) {
    allBreeds(species: $species) {
      id
      name
      species
    }
  }
`;

// ── Create pet (no device) ─────────────────────

/** Create a new pet in the rescue household without assigning a device. */
export const createPetMutation = gql`
  mutation createPet($input: CreatePetInput!) {
    createPet(input: $input) {
      id
      name
    }
  }
`;

/** Update an existing pet's profile fields. */
export const updatePetMutation = gql`
  mutation rescueUpdatePet($input: UpdatePetInput!) {
    updatePet(input: $input) {
      id
      name
    }
  }
`;

// ── Org info (lightweight) ──────────────────────

/** Lightweight query for the rescue org name. Fetches chipReseller name
 *  and falls back to the user's RESCUE-type household name. */
export const rescueOrgInfoQuery = gql`
  query rescueOrgInfo {
    currentUser {
      id
      isAdmin
      firstName
      lastName
      email
      phoneNumber
      chipReseller {
        id
        name
        types
        taxExemptType
        contactInfo {
          firstName
          lastName
        }
        shippingAddress {
          line1
          line2
          city
          state
          country
          zip
        }
        verified
      }
      userHouseholds {
        household {
          id
          name
          type
        }
      }
    }
  }
`;

export interface RescueUnassignedInventoryDeviceItemGql {
  moduleId: string;
  type?: string | null;
  batteryPercent: number | null;
  isOnline: boolean;
  lastConnectionDate: string;
}

export interface RescueUnassignedDeviceInventoryData {
  rescueUnassignedDeviceInventory: {
    items: RescueUnassignedInventoryDeviceItemGql[];
    totalCount: number;
    hasMore: boolean;
  };
}

export const rescueUnassignedDeviceInventoryQuery = gql`
  query rescueUnassignedDeviceInventory($input: RescueUnassignedInventoryDevicesInput!) {
    rescueUnassignedDeviceInventory(input: $input) {
      items {
        moduleId
        type
        batteryPercent
        isOnline
        lastConnectionDate
      }
      totalCount
      hasMore
    }
  }
`;

export interface RescueOrgInfoData {
  currentUser: {
    id: string;
    isAdmin: boolean;
    firstName: string;
    lastName: string | null;
    email: string;
    phoneNumber: string | null;
    chipReseller?: {
      id: string;
      name: string;
      types?: (string | null)[] | null;
      taxExemptType?: ResellerTaxExemptType | null;
      contactInfo?: { firstName: string; lastName: string | null } | null;
      shippingAddress?: RescueOrgShippingAddress | null;
      verified: boolean;
    } | null;
    userHouseholds: {
      household: {
        id: string;
        name: string | null;
        type: string;
      };
    }[];
  };
}

export interface ExternalChipLookupData {
  resellerChip?: {
    id: string;
    shortId: string;
    serialNumber: string;
    status: string;
    updatedAt: string;
    pet?: {
      id: string;
      name: string;
      gender: string;
      species?: string | null;
      isPurebred: boolean;
      color?: string | null;
      yearOfBirth: number;
      monthOfBirth?: number | null;
      dayOfBirth?: number | null;
      weight: number;
      additionalInfo?: string | null;
      updatedAt: string;
      breed?: { id: string; name: string } | null;
    } | null;
    latestContactHistoryEntry?: {
      source: string;
      contactInfo: {
        firstName: string;
        lastName?: string | null;
        primaryPhone?: string | null;
        primaryEmail: string;
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        state?: string | null;
        zipcode?: string | null;
      };
    } | null;
    reseller?: {
      id: string;
      name: string;
    } | null;
  } | null;
}

export interface ExternalStrangerChipLookupData {
  chip?: {
    id: string;
    serialNumber: string;
    shortId: string;
    hasOwner: boolean;
    isFiChip: boolean;
  } | null;
}
